import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc, collection, query, where, onSnapshot, getDocs, orderBy, limit } from "firebase/firestore";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";

const Dashboard = () => {
  const [user, loading] = useAuthState(auth);
  const [userData, setUserData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setLoadingData(false);
        return;
      }

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();

        // Redirect investors to their dashboard
        if (data.role === "investor") {
          navigate("/investor-dashboard", { replace: true });
          return;
        }

        // Redirect admins to the admin dashboard
        if (data.role === "admin") {
          navigate("/admin/dashboard", { replace: true });
          return;
        }

        // Founders stay on the dashboard
        setUserData(data);
      }

      setLoadingData(false);
    };

    fetchUserData();
  }, [user, navigate]);

  // Unread inbox counter: sum of unread messages across all chats for this user
  useEffect(() => {
    if (!user) return;

    const contactsRef = collection(db, "contacts");
    const qFounder = query(contactsRef, where("founderId", "==", user.uid));
    const qInvestor = query(contactsRef, where("investorId", "==", user.uid));

    let founderDocs = [];
    let investorDocs = [];

    const rebuild = async () => {
      const allDocs = [...founderDocs, ...investorDocs];
      const mapByChat = new Map();
      allDocs.forEach((d) => {
        const data = d.data();
        const otherId = data.founderId === user.uid ? data.investorId : data.founderId;
        if (!otherId) return;
        const cId = [user.uid, otherId].sort().join("_");
        mapByChat.set(cId, otherId);
      });

      let total = 0;
      await Promise.all(
        Array.from(mapByChat.entries()).map(async ([ci]) => {
          try {
            // Fetch recent messages and lastReadAt to approximate unread
            const qRecent = query(collection(db, "chats", ci, "messages"), orderBy("createdAt", "desc"), limit(50));
            const rSnap = await getDocs(qRecent);
            // Load lastReadAt
            const readSnap = await getDocs(query(collection(db, "chats", ci, "reads")));
            let lastReadAt = null;
            readSnap.forEach((docSnap) => {
              if (docSnap.id === user.uid) {
                const data = docSnap.data();
                lastReadAt = data?.lastReadAt || null;
              }
            });
            const count = rSnap.docs.reduce((acc, d) => {
              const data = d.data();
              const ts = data.createdAt?.toDate ? data.createdAt.toDate() : null;
              if (data.to === user.uid && (!lastReadAt || (ts && ts > lastReadAt))) return acc + 1;
              return acc;
            }, 0);
            total += count;
          } catch {}
        })
      );

      setUnreadCount(total);
    };

    const unsubF = onSnapshot(qFounder, (snap) => { founderDocs = snap.docs; rebuild(); });
    const unsubI = onSnapshot(qInvestor, (snap) => { investorDocs = snap.docs; rebuild(); });

    return () => { unsubF(); unsubI(); };
  }, [user]);

  if (loading || loadingData) {
    return (
      <div className="flex justify-center items-center h-screen">
        ⏳ Loading Dashboard...
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <h1 className="text-2xl font-bold">
        👑 Welcome back, {userData?.fullName || "Founder"}
      </h1>
      <p className="text-gray-600 mt-1">
        You're building something powerful. Let's connect you with investors 💰
      </p>

      {/* Profile Info Section */}
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h2 className="text-lg font-semibold mb-2">🧾 Your Profile</h2>
        {userData ? (
          <>
            <p>📧 <strong>Email:</strong> {userData.email}</p>
            <p>🧑‍💼 <strong>Full Name:</strong> {userData.fullName}</p>
            <p>🏢 <strong>Business:</strong> {userData.businessName || "Not Provided"}</p>
            <p>📍 <strong>Location:</strong> {userData.location || "Not Provided"}</p>
            <p>🏭 <strong>Industry:</strong> {userData.industry || "Not Provided"}</p>
            <p>💰 <strong>Funding Required:</strong> {userData.fundingRequired || "Not Provided"}</p>
            <p>📦 <strong>Plan:</strong> {userData.plan || "None"}</p>
            <p>💬 <strong>Investor Contacts:</strong> {userData.contactsRemaining ?? 0} Contacts Remaining</p>
          </>
        ) : (
          <p className="text-gray-500 italic">Loading profile...</p>
        )}
      </div>

      {/* Buttons */}
      <div className="mt-8 flex flex-col gap-4 w-full max-w-xs">
        <button
          onClick={() => navigate("/edit-profile")}
          className="bg-yellow-500 text-white py-2 rounded hover:bg-yellow-600"
        >
          ✏️ Edit Profile
        </button>
        <button
          onClick={() => navigate("/inbox")}
          className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700 flex items-center justify-center gap-2"
          aria-label={`Inbox${unreadCount > 0 ? ' ' + unreadCount : ''}`}
        >
          <span>💬 Inbox{unreadCount > 0 ? ` ${unreadCount}` : ""}</span>
        </button>
        <button
          onClick={() => navigate("/browse-investors")}
          className="bg-green-600 text-white py-2 rounded hover:bg-green-700"
        >
          🌍 Browse Investors
        </button>
        <button
          onClick={handleLogout}
          className="bg-red-600 text-white py-2 rounded hover:bg-red-700"
        >
          🔴 Logout
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
